import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, Stepper, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { savePendingConnect } from "../meta-callback/pending-connect";
import { isPlanGateError } from "../shared/plan-gate";
import type { ChannelPageContext } from "./channel-context";
import { useMessengerConnect, useMessengerDisconnect } from "./use-channels";

const P = "deployKnowledge.channels.messenger";
const SERVICE_UNAVAILABLE = 503;
export const META_PERMISSIONS = [
  { scope: "pages_messaging", key: "permMessaging" },
  { scope: "pages_manage_metadata", key: "permMetadata" },
  { scope: "pages_show_list", key: "permList" },
  { scope: "business_management", key: "permBusiness" },
] as const;

/** Steps shared with the callback page: 1 Log in · 2 Pick Page · 3 Permissions. */
export function useMessengerSteps() {
  const { t } = useTranslation();
  return [
    { id: "login", label: t(`${P}.stepLogin`) },
    { id: "page", label: t(`${P}.stepPage`) },
    { id: "permissions", label: t(`${P}.stepPermissions`) },
  ];
}

export function MetaPermissionsList() {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2.5 text-sm">
      {META_PERMISSIONS.map((p) => (
        <li key={p.scope} className="flex items-start gap-2.5">
          <Icon name="check" className="mt-0.5 size-4 text-success" />
          <span>
            <span className="font-mono text-xs">{p.scope}</span>
            <span className="block text-xs text-fg-subtle">{t(`${P}.${p.key}`)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Never navigate to anything but Facebook's https OAuth dialog. */
export function isFacebookUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && /(^|\.)facebook\.com$/.test(url.hostname);
  } catch {
    return false;
  }
}

interface ConnectDialogProps {
  ctx: ChannelPageContext;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Same-tab redirect (rule 8: no auth popups). Injected for tests. */
  redirect?: (url: string) => void;
}

export function MessengerConnectDialog({ ctx, open, onOpenChange, redirect = (url) => { window.location.assign(url); } }: ConnectDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const connect = useMessengerConnect(ctx.agentId);
  const steps = useMessengerSteps();
  const [redirecting, setRedirecting] = useState(false);
  const start = () => {
    connect.mutate(undefined, {
      onSuccess: ({ authorize_url }) => {
        if (!isFacebookUrl(authorize_url)) {
          toast.error(t("deployKnowledge.channels.errors.notConfigured"));
          return;
        }
        savePendingConnect({ wsSlug: ctx.wsSlug, wsId: ctx.wsId, agentId: ctx.agentId });
        setRedirecting(true);
        redirect(authorize_url);
      },
      onError: (error) => {
        if (isPlanGateError(error)) {
          onOpenChange(false);
          ctx.onPlanGate("messenger");
        } else if (isApiError(error) && error.status === SERVICE_UNAVAILABLE) toast.error(t("deployKnowledge.channels.errors.notConfigured"));
        else toast.error(message(error));
      },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t(`${P}.dialogTitle`)} closeLabel={t("common.close")}>
        <div className="space-y-4">
          <Stepper steps={steps} current={0} aria-label={t(`${P}.dialogTitle`)} />
          <p className="text-sm text-fg-muted">{t(`${P}.dialogBody`)}</p>
          <p className="text-sm text-fg-muted">{t(`${P}.permissionsIntro`)}</p>
          <MetaPermissionsList />
          <div className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${P}.pauseNote`)}</div>
          <Button size="lg" className="w-full" onClick={start} loading={connect.isPending || redirecting}>
            <Icon name="log-in" />
            {connect.isPending || redirecting ? t(`${P}.redirecting`) : t(`${P}.continueWithFacebook`)}
          </Button>
          <p className="text-xs text-fg-subtle">{t(`${P}.redirectNote`)}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface DisconnectDialogProps {
  ctx: ChannelPageContext;
  pageName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessengerDisconnectDialog({ ctx, pageName, open, onOpenChange }: DisconnectDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const disconnect = useMessengerDisconnect(ctx.agentId);
  const confirm = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        toast.success(t(`${P}.disconnected`));
        onOpenChange(false);
      },
      onError: (error) => toast.error(message(error)),
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t(`${P}.disconnectTitle`)}
        description={t(`${P}.disconnectBody`, { page: pageName ?? t(`${P}.thisPage`) })}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" onClick={confirm} loading={disconnect.isPending}>
              {t(`${P}.disconnectConfirm`)}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
